import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export default function BackgroundRotator({
  images = [],
  durationMs = 3500,
  fadeMs = 700,
  scale = 1,
  className = '',
  style = {},
}) {
  const normalizedImages = useMemo(
    () => (Array.isArray(images) ? images.filter(Boolean) : []),
    [images],
  )

  const [index, setIndex] = useState(0)
  const loadedSet = useRef(new Set())
  const [loaded, setLoaded] = useState(() => new Set())

  const preload = useCallback((src) => {
    if (!src || loadedSet.current.has(src)) return
    loadedSet.current.add(src)
    const img = new window.Image()
    img.src = src
    img.onload = () => setLoaded((prev) => new Set(prev).add(src))
  }, [])

  useEffect(() => {
    if (normalizedImages.length === 0) return
    preload(normalizedImages[index])
    const next = (index + 1) % normalizedImages.length
    if (next !== index) preload(normalizedImages[next])
  }, [normalizedImages, index, preload])

  useEffect(() => {
    if (normalizedImages.length <= 1) return
    if (!Number.isFinite(durationMs) || durationMs <= 0) return

    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % normalizedImages.length)
    }, durationMs)

    return () => window.clearInterval(id)
  }, [normalizedImages.length, durationMs])

  if (normalizedImages.length === 0) return null

  return (
    <div className={`bgRotator ${className}`.trim()} style={style}>
      {normalizedImages.map((src, i) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={`${src}-${i}`}
          className="bgRotator__layer"
          style={{
            backgroundImage: loaded.has(src) ? `url("${src}")` : undefined,
            opacity: i === index ? 1 : 0,
            transitionDuration: `${fadeMs}ms`,
            '--bg-scale': 0.5,
          }}
        />
      ))}
    </div>
  )
}

